import React, { useState, useContext, useEffect, useCallback, useRef } from 'react'
import { ModalOverlay, ModalWrapper } from './style'
import { useOutletContext, useParams } from 'react-router-dom'
import { type DefaultValues } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import OrdenoLoading from '../../../../components/OrdenoLoading'
import AddProductForm from '../AddProductForm'
import AttachmentManager from '../../../../components/AttachmentManager'
import ApiService from '../../../../services/ApiService'
import MainInfos from '../../OrderForm/Sections/MainInfos'
import Details from '../../OrderForm/Sections/Details'
import { Card, FormGroup } from '../../OrderForm/styles/shared'
import { Label } from '../../OrderForm/Sections/Details.style'
import { ORDER_SECTION_IDS } from '../../OrderForm/constants'
import { FormContent, ProductApiData } from './types'
import { FashionOrderFormValues, fashionOrderSchema } from './fashionOrder.schema'
import OrderForm from '../../OrderForm'
import FashionAutofillBridge from './components/FashionAutofillBridge'
import FashionHydrationBridge from './components/FashionHydrationBridge'
import FashionProductModal from './components/FashionProductModal'
import FashionOrderSaveBridge from './components/FashionOrderSaveBridge'
import FashionOrderStepper from './components/FashionOrderStepper'
import { fashionItemsModule } from './fashionItemsModule'
import { createFashionOrderDefaults } from './constants'
import { buildOrderPayload, fetchFashionOrderById } from './api'
import { OrderItemsProvider, useOrderItemsState } from './contexts/OrderItemsContext'
import { useAttachmentHandler } from './Hooks/useAttachmentHandler'
import { useInstallments } from './Hooks/useInstallments'
import ItemsSection from './Sections/Items'
import PaymentSection from './Sections/PaymentSection'
import ShippingSection from './Sections/ShippingSection'
import { formatFileSize } from './utils'

const RegisterFashionOrder: React.FC = () => {
  const { orderId } = useParams<{ orderId?: string }>()
  const isEditing = !!orderId

  const outletContext = useOutletContext<FormContent>()
  const { setFeedback } = useContext(FeedbackContext)

  const [loading, setLoading] = useState(isEditing)

  const isInitialLoad = useRef(true)

  const [loadedValues, setLoadedValues] = useState<FashionOrderFormValues | undefined>()
  const [loadedItems, setLoadedItems] = useState<{ products: ProductApiData[]; kitCount: number } | undefined>()

  const attachmentsApi = useAttachmentHandler()
  const {
    selectedFiles, existingFileIds, removedFileIds,
    setSelectedFiles, setExistingAttachments, setExistingFileIds, setRemovedFileIds,
    uploadPendingFiles,
  } = attachmentsApi


  useEffect(() => {
    if (!isEditing) return
    const loadOrder = async () => {
      setLoading(true)
      try {
        const loaded = await fetchFashionOrderById(Number(orderId))

        setExistingAttachments(loaded.attachments)
        setExistingFileIds(loaded.attachments.map((a) => a.id))
        setRemovedFileIds([])
        setLoadedItems({ products: loaded.products, kitCount: loaded.kitCount })
        setLoadedValues({
          ...loaded.values,
          paymentMethodId: loaded.paymentMethodId ?? undefined,
          installments: loaded.installments,
        } as FashionOrderFormValues)
        isInitialLoad.current = false
      } catch (error) {
        console.error('Erro ao buscar o pedido:', error)
      }
      setLoading(false)
    }
    loadOrder()
  }, [orderId, isEditing, setExistingAttachments, setExistingFileIds, setRemovedFileIds])





  const withSaving = useCallback(
    async (fn: () => Promise<void>) => {
      outletContext?.setIsSaving?.(true)
      try { await fn() } finally { outletContext?.setIsSaving?.(false) }
    },
    [outletContext]
  )

  const handleSaveOrder = useCallback(async (payload: any) => {
    try {
      await withSaving(async () => {
        const response = await ApiService.steelOrders.createOrder(payload)
        const createdOrderId = response.data?.id
        if (selectedFiles.length > 0 && createdOrderId) {
          const newFileIds = await uploadPendingFiles()
          const keptExistingIds = existingFileIds.filter((id) => !removedFileIds.includes(id))
          const allFileIds = [...keptExistingIds, ...newFileIds]
          await ApiService.steelOrders.updateOrder(createdOrderId, {
            fileIds: allFileIds
          })
        } else {
          setSelectedFiles([])
        }
        setFeedback({ isOpen: true, message: 'Pedido criado com sucesso!', statusOk: true })
      })
    } catch (err: any) {
      const backendMessage = err.response?.data?.message
      const displayMessage = Array.isArray(backendMessage)
        ? backendMessage.join(', ')
        : (backendMessage || err.message || 'Erro ao salvar pedido')
      setFeedback({
        isOpen: true,
        message: displayMessage,
        statusOk: false,
      })
      throw err
    }
  }, [uploadPendingFiles, selectedFiles, existingFileIds, removedFileIds, setFeedback, withSaving])

  const handleEditSaveOrder = useCallback(async (payload: any) => {
    try {
      await withSaving(async () => {
        const { saleStatus, ...patchPayload } = payload
        await ApiService.steelOrders.updateOrder(String(orderId), patchPayload)
        if (selectedFiles.length > 0) {
          const newFileIds = await uploadPendingFiles()
          const keptExistingIds = existingFileIds.filter((id) => !removedFileIds.includes(id))
          const allFileIds = [...keptExistingIds, ...newFileIds]
          await ApiService.steelOrders.updateOrder(String(orderId), {
            fileIds: allFileIds
          })
        } else {
          setSelectedFiles([])
        }
        setFeedback({ isOpen: true, message: 'Pedido atualizado com sucesso!', statusOk: true })
      })
    } catch (err: any) {
      const backendMessage = err.response?.data?.message
      const displayMessage = Array.isArray(backendMessage)
        ? backendMessage.join(', ')
        : (backendMessage || err.message || 'Erro ao atualizar o pedido')
      setFeedback({ isOpen: true, message: displayMessage, statusOk: false })
      throw err
    }
  }, [orderId, uploadPendingFiles, selectedFiles, existingFileIds, removedFileIds, setFeedback, withSaving])



  if (loading) return <OrdenoLoading variant="fullscreen" text="Carregando pedido..." />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <OrderForm<FashionOrderFormValues>
        resolver={zodResolver(fashionOrderSchema)}
        defaultValues={createFashionOrderDefaults() as DefaultValues<FashionOrderFormValues>}
        loadedValues={loadedValues}
        itemsModule={fashionItemsModule}
        supplier={{ apiUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`, queryParams: { isMain: 'true' } }}
        seller={{ apiUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll?filters=${encodeURIComponent(JSON.stringify([{ field: 'Perfil', type: 'Contém', text: 'VENDEDOR' }]))}` }}
        client={{ apiUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`, queryParams: { companyType: 'Cliente', excludeMainCompany: 'true' }, allowCreate: true }}
        stepper={<FashionOrderStepper />}
        installmentsSlot={<PaymentSection isEditing={isEditing} />}
        attachmentsSlot={(
          <FormGroup>
            <Label>Anexos</Label>
            <AttachmentManager
              selectedFiles={attachmentsApi.selectedFiles}
              existingAttachments={attachmentsApi.existingAttachments}
              onFileChange={attachmentsApi.handleFileChange}
              onRemoveFile={attachmentsApi.handleRemoveFile}
              onViewAttachment={attachmentsApi.handleViewAttachment}
              onDownloadAttachment={attachmentsApi.handleDownloadAttachment}
              onRemoveExistingAttachment={isEditing ? attachmentsApi.handleRemoveExistingAttachment : undefined}
              onSelectFromGallery={attachmentsApi.handleSelectFromGallery}
              formatFileSize={formatFileSize}
              onPreviewFile={attachmentsApi.handlePreviewFile}
              onDownloadFile={attachmentsApi.handleDownloadFile}
            />
          </FormGroup>
        )}
      >
        <FashionAutofillBridge isEditing={isEditing} />
        <FashionHydrationBridge loadedItems={loadedItems} />
        <FashionOrderSaveBridge
          target={isEditing ? outletContext?.handleEditSave : outletContext?.handleSave}
          fileIds={existingFileIds.filter((id) => !removedFileIds.includes(id))}
          submit={isEditing ? handleEditSaveOrder : handleSaveOrder}
          onGuardFailure={(message) => setFeedback({ isOpen: true, message, statusOk: false })}
          onInvalid={() => setFeedback({ isOpen: true, message: 'Existem erros no formulário. Revise os campos destacados.', statusOk: false })}
        />
        <FashionProductModal />
      </OrderForm>
    </div>
  )
}

export default RegisterFashionOrder